import { buildApiUrl } from "./apiConfig";
import { apiClient } from "./apiClient";

/** US postal: digits only, max 9 (ZIP+4). */
export function normalizeUsPostalDigits(raw: string | undefined | null): string {
    return (raw ?? "").replace(/\D/g, "").slice(0, 9);
}

export type ZipLookupResponse = {
    postalCode: string;
    stateId: number | null;
    cityId: number | null;
    stateName: string | null;
    cityName: string | null;
};

/**
 * Resolves city/state (and internal ids) from a US ZIP via GET /api/addresses/lookup-from-zip.
 */
export async function lookupAddressFromZip(zipInput: string): Promise<
    | { ok: true; data: ZipLookupResponse }
    | { ok: false; error: string; status: number }
> {
    const digitsOnly = normalizeUsPostalDigits(zipInput);
    const zip5 = digitsOnly.slice(0, 5);
    if (zip5.length !== 5) {
        return {
            ok: false,
            error: "Enter a valid U.S. ZIP (at least 5 digits). You can paste ZIP+4 or text that includes a ZIP.",
            status: 400,
        };
    }

    const url = buildApiUrl(`api/addresses/lookup-from-zip?zip=${encodeURIComponent(zip5)}`);
    const res = await apiClient(url, {
        method: "GET",
        headers: { Accept: "application/json" },
        credentials: "include",
    });

    const text = await res.text();
    let body: Record<string, unknown> | null = null;
    try {
        body = text ? (JSON.parse(text) as Record<string, unknown>) : null;
    } catch {
        /* non-json */
    }

    if (!res.ok) {
        const msg =
            (typeof body?.detail === "string" && body.detail) ||
            (typeof body?.message === "string" && body.message) ||
            `Address lookup failed (${res.status}).`;
        return { ok: false, error: msg, status: res.status };
    }

    return { ok: true, data: body as unknown as ZipLookupResponse };
}
